// Everything Oreia has ever held about you, filed under what you said back.
//
// This is the other half of the privacy promise. "Nothing is hidden" is only true if there is a
// place you can go and see the whole list, including the things you rejected — otherwise "reject"
// just means "hide", and you would have no way to check that a no was actually taken.
//
// One thing genuinely is absent, and correctly so: anything you asked Oreia to forget. The server
// clears the ciphertext on a forget, so those rows come back unreadable and are dropped upstream in
// the API layer. A "forgotten" bucket listing what was forgotten would defeat the point of it.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import { fetchObservationHistory, type Observation } from '../api/portrait';
import { bucketHistory, type ArchiveBuckets } from './review-deck';
import { CitedObservation } from '../components/CitedObservation';
import { EmptyState, Spinner } from '../components/ui';

const NOTICED = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** When Oreia first wrote it. The year is always shown — an archive is read across years. */
function noticedOn(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : NOTICED.format(date);
}

export function ArchiveScreen() {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [buckets, setBuckets] = useState<ArchiveBuckets | null>(null);

  useEffect(() => {
    if (!dek) return;
    const controller = new AbortController();
    fetchObservationHistory(dek, controller.signal)
      .then((all) => {
        if (!controller.signal.aborted) setBuckets(bucketHistory(all));
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setBuckets({ confirmed: [], rejected: [], setAside: [] });
        }
      });
    return () => controller.abort();
  }, [dek]);

  if (!dek || !buckets) {
    return (
      <div className="oscreen oarchive">
        <Spinner label="Opening the archive…" />
      </div>
    );
  }

  const total = buckets.confirmed.length + buckets.rejected.length + buckets.setAside.length;

  return (
    <div className="oscreen oarchive">
      <div className="oreview-head">
        <Link className="oreview-back" to="/self">
          Self
        </Link>
        <h1 className="otitle">Everything Oreia has held</h1>
        <p className="obody">
          Every observation it has written about you, filed under what you said back. Nothing here is
          shared with anyone.
        </p>
      </div>

      {total === 0 ? (
        <EmptyState
          title="Nothing filed yet"
          body="Once you review what Oreia notices, your answers collect here."
        />
      ) : (
        <>
          <Bucket
            label="Confirmed"
            note="You said these fit. Oreia leans on them."
            cards={buckets.confirmed}
          />
          <Bucket
            label="Not me"
            note="You said these miss. Oreia stopped holding them, and learns from the no."
            cards={buckets.rejected}
          />
          <Bucket
            label="Set aside"
            note="You didn’t want to decide. They stay here rather than following you around."
            cards={buckets.setAside}
          />
        </>
      )}
    </div>
  );
}

function Bucket({ label, note, cards }: { label: string; note: string; cards: Observation[] }) {
  if (cards.length === 0) return null;

  return (
    <section className="oarchive-bucket">
      <div className="asec">
        <h2>{label}</h2>
        <span className="acount2">{cards.length}</span>
      </div>
      <p className="oarchive-note">{note}</p>

      <ul className="oarchive-list">
        {cards.map((card) => (
          <li key={card.id}>
            <CitedObservation as="div" className="oarchive-text" text={card.text} citations={card.citations} />
            <span className="omono">
              {card.userEdited ? 'in your words · ' : ''}
              {noticedOn(card.createdAt)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
